import { CUSTOMER_EXTERNAL_REFERENCE_KEY, getCustomerByExternalReference, } from "../../../customer/index.js";
import { fetchSubscriptionVariantsBySku } from "../../../subscription-product/queries/fetchSubscriptionVariants.pim.js";
import { getAllSubscriptionContracts } from "../../../subscription-contract/queries/getAllSubscriptionContracts.subscriptions.js";
import { isAlreadySubscribed } from "../../../subscription-contract/utils/isAlreadySubscribed.js";
/**
 * Normalize a single name part (trim, collapse spaces, remove diacritics, lowercase)
 */
function normalizeName(name) {
    const base = (name ?? "").trim().replace(/\s+/g, " ");
    const noDiacritics = base.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return noDiacritics.toLowerCase();
}
/**
 * Build and normalize a full name from firstname and lastname
 */
function normalizeFullName(firstname, lastname) {
    const combined = [firstname ?? "", lastname ?? ""]
        .filter(Boolean)
        .join(" ")
        .trim();
    return normalizeName(combined);
}
/**
 * Validates against an existing customer found by phone, and subscription duplication by SKU.
 * - If a customer exists, compare imported first_name / last_name with existing customer names.
 * - Also compare billing_first_name / billing_last_name with existing customer names.
 * - If the customer already has this subscription, add an error on sku.
 */
export const validateExistingCustomerAndSubscription = async (contract, errors, client) => {
    const phone = (contract.phone_number ?? "").trim();
    const sku = (contract.sku ?? "").trim();
    if (!phone || !sku) {
        return errors;
    }
    // Lookup existing customer by phone external reference
    const customer = await getCustomerByExternalReference({ value: phone, key: CUSTOMER_EXTERNAL_REFERENCE_KEY.PHONE }, { client });
    if (contract.source === "SALTO") {
        if (customer) {
            const existingFullName = normalizeFullName(customer.firstName, customer.lastName);
            const contractFullName = normalizeFullName(contract.first_name, contract.last_name);
            if (contractFullName &&
                existingFullName &&
                contractFullName !== existingFullName) {
                const nameMismatchMsg = "Kontraktnavn stemmer ikke overens med eksisterende kundenavn i Crystallize.";
                errors.first_name = errors.first_name ?? [];
                if (!errors.first_name.includes(nameMismatchMsg)) {
                    errors.first_name.push(nameMismatchMsg);
                }
                errors.last_name = errors.last_name ?? [];
                if (!errors.last_name.includes(nameMismatchMsg)) {
                    errors.last_name.push(nameMismatchMsg);
                }
            }
            const billingFullName = normalizeFullName(contract.billing_first_name, contract.billing_last_name);
            if (contractFullName &&
                billingFullName &&
                contractFullName !== billingFullName) {
                const billingMismatchMsg = "Faktureringsnavn stemmer ikke overens med navn på kunden i Crystallize.";
                errors.billing_first_name = errors.billing_first_name ?? [];
                if (!errors.billing_first_name.includes(billingMismatchMsg)) {
                    errors.billing_first_name.push(billingMismatchMsg);
                }
                errors.billing_last_name = errors.billing_last_name ?? [];
                if (!errors.billing_last_name.includes(billingMismatchMsg)) {
                    errors.billing_last_name.push(billingMismatchMsg);
                }
            }
        }
    }
    if (!customer?.identifier) {
        return errors;
    }
    // Check if already subscribed to this SKU
    const existingContractsSkus = await getAllSubscriptionContracts({ client }, customer.identifier);
    if (!existingContractsSkus?.length) {
        return errors;
    }
    const subscriptionVariantsSkus = await fetchSubscriptionVariantsBySku({ sku }, { client });
    const hasSubscribed = isAlreadySubscribed(subscriptionVariantsSkus, existingContractsSkus);
    if (hasSubscribed) {
        const hasSubscribedMsg = "Already has this subscription";
        errors.sku = errors.sku ?? [];
        if (!errors.sku.includes(hasSubscribedMsg)) {
            errors.sku.push(hasSubscribedMsg);
        }
    }
    return errors;
};
